import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Animated, Easing, Dimensions } from 'react-native';
import { Text, Card, useTheme, Button, IconButton } from 'react-native-paper';
import type { MasteryState } from '@coffeeplexity/shared';

const { width: screenWidth } = Dimensions.get('window');

interface ProgressUpdate {
  skillId: string;
  skillName: string;
  oldMastery: number;
  newMastery: number;
  explanation: string;
  aiInsights: string[];
  milestone?: string;
  nextGoal?: string;
}

interface AnimatedProgressUpdateProps {
  progressUpdate: ProgressUpdate;
  onComplete: () => void;
  onSkillSelect?: (skillId: string) => void;
  autoPlay?: boolean;
  style?: any;
}

export function AnimatedProgressUpdate({
  progressUpdate,
  onComplete,
  onSkillSelect,
  autoPlay = true,
  style,
}: AnimatedProgressUpdateProps) {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);

  // Animation values
  const slideAnimation = useRef(new Animated.Value(0)).current;
  const progressAnimation = useRef(
    new Animated.Value(progressUpdate.oldMastery)
  ).current;
  const scaleAnimation = useRef(new Animated.Value(0)).current;
  const glowAnimation = useRef(new Animated.Value(0)).current;
  const celebrationAnimation = useRef(new Animated.Value(0)).current;

  const steps = ['intro', 'progress', 'explanation', 'celebration', 'complete'];

  useEffect(() => {
    if (autoPlay) {
      startAnimation();
    }
  }, [autoPlay]);

  const startAnimation = () => {
    // Initial slide in
    Animated.timing(slideAnimation, {
      toValue: 1,
      duration: 500,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      // Start progress animation after a brief pause
      setTimeout(() => {
        animateProgress();
      }, 800);
    });

    // Scale in animation
    Animated.timing(scaleAnimation, {
      toValue: 1,
      duration: 600,
      easing: Easing.back(1.2),
      useNativeDriver: true,
    }).start();
  };

  const animateProgress = () => {
    setCurrentStep(1);

    // Animate progress bar
    Animated.timing(progressAnimation, {
      toValue: progressUpdate.newMastery,
      duration: 2000,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start(() => {
      // Show explanation after progress animation
      setTimeout(() => {
        setCurrentStep(2);
        setShowExplanation(true);

        // Start celebration if it's a significant improvement
        if (progressUpdate.newMastery - progressUpdate.oldMastery > 0.1) {
          startCelebration();
        } else {
          setTimeout(() => setCurrentStep(4), 2000);
        }
      }, 500);
    });

    // Glow effect during progress
    Animated.loop(
      Animated.sequence([
        Animated.timing(glowAnimation, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: false,
        }),
        Animated.timing(glowAnimation, {
          toValue: 0,
          duration: 1000,
          useNativeDriver: false,
        }),
      ])
    ).start();
  };

  const startCelebration = () => {
    setCurrentStep(3);

    // Celebration animation
    Animated.sequence([
      Animated.timing(celebrationAnimation, {
        toValue: 1,
        duration: 800,
        easing: Easing.out(Easing.back(1.5)),
        useNativeDriver: true,
      }),
      Animated.delay(1500),
      Animated.timing(celebrationAnimation, {
        toValue: 0,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setCurrentStep(4);
    });
  };

  const handleComplete = () => {
    // Slide out animation
    Animated.timing(slideAnimation, {
      toValue: 0,
      duration: 400,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      onComplete();
    });
  };

  const getMasteryLevelInfo = (level: number) => {
    if (level >= 0.9) return { label: 'Expert', color: '#1B5E20', icon: '🏆' };
    if (level >= 0.8)
      return { label: 'Advanced', color: '#2E7D32', icon: '⭐' };
    if (level >= 0.6)
      return { label: 'Proficient', color: '#388E3C', icon: '✅' };
    if (level >= 0.4)
      return { label: 'Intermediate', color: '#FBC02D', icon: '📈' };
    if (level >= 0.2)
      return { label: 'Beginner', color: '#F57C00', icon: '🌱' };
    return { label: 'Novice', color: '#D32F2F', icon: '🎯' };
  };

  const oldLevelInfo = getMasteryLevelInfo(progressUpdate.oldMastery);
  const newLevelInfo = getMasteryLevelInfo(progressUpdate.newMastery);
  const improvement = progressUpdate.newMastery - progressUpdate.oldMastery;

  const glowColor = glowAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: ['rgba(76, 175, 80, 0)', 'rgba(76, 175, 80, 0.4)'],
  });

  const slideTransform = slideAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [screenWidth, 0],
  });

  return (
    <Animated.View
      style={[
        styles.container,
        style,
        {
          transform: [
            { translateX: slideTransform },
            { scale: scaleAnimation },
          ],
          shadowColor: glowColor,
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 1,
          shadowRadius: 20,
        },
      ]}
    >
      <Card style={styles.card} mode='elevated'>
        <Card.Content style={styles.content}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerContent}>
              <Text variant='headlineSmall' style={styles.title}>
                Skill Progress Update
              </Text>
              <IconButton
                icon='close'
                size={20}
                onPress={handleComplete}
                style={styles.closeButton}
              />
            </View>

            <Text variant='titleMedium' style={styles.skillName}>
              {progressUpdate.skillName}
            </Text>
          </View>

          {/* Progress Visualization */}
          <View style={styles.progressSection}>
            <View style={styles.progressHeader}>
              <View style={styles.levelBadge}>
                <Text style={styles.levelIcon}>{oldLevelInfo.icon}</Text>
                <Text
                  variant='bodySmall'
                  style={[styles.levelText, { color: oldLevelInfo.color }]}
                >
                  {oldLevelInfo.label}
                </Text>
              </View>

              <View style={styles.arrow}>
                <Text style={styles.arrowText}>→</Text>
              </View>

              <View style={styles.levelBadge}>
                <Text style={styles.levelIcon}>{newLevelInfo.icon}</Text>
                <Text
                  variant='bodySmall'
                  style={[styles.levelText, { color: newLevelInfo.color }]}
                >
                  {newLevelInfo.label}
                </Text>
              </View>
            </View>

            {/* Animated Progress Bar */}
            <View style={styles.progressBarContainer}>
              <View style={styles.progressLabels}>
                <Text variant='bodySmall' style={styles.progressLabel}>
                  {Math.round(progressUpdate.oldMastery * 100)}%
                </Text>
                <Text
                  variant='bodySmall'
                  style={[styles.progressLabel, { color: newLevelInfo.color }]}
                >
                  {Math.round(progressUpdate.newMastery * 100)}%
                </Text>
              </View>

              <View style={styles.progressTrack}>
                {/* Background track */}
                <View
                  style={[
                    styles.progressBackground,
                    { backgroundColor: theme.colors.surfaceVariant },
                  ]}
                />

                {/* Old progress (static) */}
                <View
                  style={[
                    styles.progressFill,
                    {
                      width: `${progressUpdate.oldMastery * 100}%`,
                      backgroundColor: oldLevelInfo.color + '40',
                    },
                  ]}
                />

                {/* Animated new progress */}
                <Animated.View
                  style={[
                    styles.progressFill,
                    {
                      width: progressAnimation.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['0%', '100%'],
                      }),
                      backgroundColor: newLevelInfo.color,
                    },
                  ]}
                />
              </View>

              {/* Improvement indicator */}
              <View style={styles.improvementIndicator}>
                <Text
                  style={[
                    styles.improvementText,
                    { color: newLevelInfo.color },
                  ]}
                >
                  +{Math.round(improvement * 100)}% improvement
                </Text>
              </View>
            </View>
          </View>

          {/* Explanation Section */}
          {showExplanation && (
            <Animated.View
              style={[
                styles.explanationSection,
                {
                  opacity: slideAnimation,
                  transform: [
                    {
                      translateY: slideAnimation.interpolate({
                        inputRange: [0, 1],
                        outputRange: [20, 0],
                      }),
                    },
                  ],
                },
              ]}
            >
              <Text variant='titleSmall' style={styles.explanationTitle}>
                What contributed to this progress?
              </Text>

              <Text variant='bodyMedium' style={styles.explanationText}>
                {progressUpdate.explanation}
              </Text>

              {progressUpdate.aiInsights.length > 0 && (
                <View style={styles.insightsSection}>
                  <Text variant='bodySmall' style={styles.insightsTitle}>
                    AI Insights:
                  </Text>
                  {progressUpdate.aiInsights.map((insight, index) => (
                    <Text
                      key={index}
                      variant='bodySmall'
                      style={styles.insightText}
                    >
                      • {insight}
                    </Text>
                  ))}
                </View>
              )}
            </Animated.View>
          )}

          {/* Milestone Celebration */}
          {currentStep === 3 && progressUpdate.milestone && (
            <Animated.View
              style={[
                styles.celebrationSection,
                {
                  transform: [
                    { scale: celebrationAnimation },
                    {
                      rotate: celebrationAnimation.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['0deg', '5deg'],
                      }),
                    },
                  ],
                },
              ]}
            >
              <Text style={styles.celebrationIcon}>🎉</Text>
              <Text variant='titleMedium' style={styles.milestoneTitle}>
                Milestone Achieved!
              </Text>
              <Text variant='bodyMedium' style={styles.milestoneText}>
                {progressUpdate.milestone}
              </Text>
            </Animated.View>
          )}

          {/* Next Goal */}
          {progressUpdate.nextGoal && currentStep >= 3 && (
            <View style={styles.nextGoalSection}>
              <Text variant='bodySmall' style={styles.nextGoalTitle}>
                Next Goal:
              </Text>
              <Text variant='bodyMedium' style={styles.nextGoalText}>
                {progressUpdate.nextGoal}
              </Text>
            </View>
          )}

          {/* Action Buttons */}
          {currentStep >= 4 && (
            <View style={styles.actionButtons}>
              {onSkillSelect && (
                <Button
                  mode='outlined'
                  onPress={() => onSkillSelect(progressUpdate.skillId)}
                  style={styles.actionButton}
                >
                  View Details
                </Button>
              )}

              <Button
                mode='contained'
                onPress={handleComplete}
                style={styles.actionButton}
              >
                Continue
              </Button>
            </View>
          )}
        </Card.Content>
      </Card>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1000,
  },
  card: {
    width: screenWidth - 32,
    maxHeight: '80%',
  },
  content: {
    padding: 24,
  },
  header: {
    marginBottom: 24,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    fontWeight: '600',
  },
  closeButton: {
    margin: 0,
  },
  skillName: {
    fontWeight: '600',
    color: '#1976D2',
  },
  progressSection: {
    marginBottom: 24,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  levelBadge: {
    alignItems: 'center',
    flex: 1,
  },
  levelIcon: {
    fontSize: 24,
    marginBottom: 4,
  },
  levelText: {
    fontWeight: '600',
  },
  arrow: {
    flex: 0.5,
    alignItems: 'center',
  },
  arrowText: {
    fontSize: 24,
    opacity: 0.6,
  },
  progressBarContainer: {
    marginBottom: 16,
  },
  progressLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  progressLabel: {
    fontWeight: '600',
  },
  progressTrack: {
    height: 12,
    borderRadius: 6,
    position: 'relative',
    overflow: 'hidden',
  },
  progressBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  progressFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    borderRadius: 6,
  },
  improvementIndicator: {
    alignItems: 'center',
    marginTop: 12,
  },
  improvementText: {
    fontWeight: '600',
    fontSize: 16,
  },
  explanationSection: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: 'rgba(25, 118, 210, 0.05)',
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#1976D2',
  },
  explanationTitle: {
    fontWeight: '600',
    marginBottom: 8,
    color: '#1976D2',
  },
  explanationText: {
    lineHeight: 20,
    marginBottom: 12,
  },
  insightsSection: {
    marginTop: 8,
  },
  insightsTitle: {
    fontWeight: '600',
    marginBottom: 6,
    color: '#1976D2',
  },
  insightText: {
    opacity: 0.8,
    marginBottom: 2,
    paddingLeft: 8,
  },
  celebrationSection: {
    alignItems: 'center',
    padding: 20,
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    borderRadius: 12,
    marginBottom: 20,
  },
  celebrationIcon: {
    fontSize: 48,
    marginBottom: 8,
  },
  milestoneTitle: {
    fontWeight: '700',
    color: '#4CAF50',
    marginBottom: 8,
  },
  milestoneText: {
    textAlign: 'center',
    color: '#4CAF50',
  },
  nextGoalSection: {
    padding: 12,
    backgroundColor: 'rgba(123, 31, 162, 0.05)',
    borderRadius: 8,
    marginBottom: 20,
  },
  nextGoalTitle: {
    fontWeight: '600',
    color: '#7B1FA2',
    marginBottom: 4,
  },
  nextGoalText: {
    color: '#7B1FA2',
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },
});
